import { QueryFunctionContext } from '@tanstack/react-query';
import { expand } from 'uri-template-lite';
import { apiClient } from '../../common/ApiClient';
import {
  AUTOCOMPLETE_PAGE_SIZE,
  OptionsPage,
  OptionType,
  Page,
} from '../../common/queryClient';
import { ClassifierMappingSearchResultType } from '../types';
import { ClassifierMappingUrl } from './ClassifierMappingUrl';
import { useInfiniteSearchQuery } from '../../common/queryClient/useInfiniteSearchQuery';

export interface ClassifierMappingSearchQueryParams {
  searchText: string;
  classifierIdFrom?: number;
  classifierCodeFrom?: string;
  selectedMapping?: string;
  page?: number;
  size?: number;
}

export interface ClassifierMappingSearchOptions {
  classifierIdFrom?: number;
  classifierCodeFrom?: string;
  selectedMapping?: string;
  getOptionFromSearchResult?: GetOptionFromSearchResult;
}

const searchClassifierMappings = ({
  searchText,
  classifierIdFrom,
  classifierCodeFrom,
  selectedMapping,
  page,
  size,
}: ClassifierMappingSearchQueryParams) =>
  apiClient
    .get<Page<ClassifierMappingSearchResultType>>(
      expand(ClassifierMappingUrl.SEARCH, {
        searchText,
        classifierIdFrom,
        classifierCodeFrom,
        selectedMapping,
        page,
        size,
      }),
    )
    .then((response) => response.data);

const searchClassifierMappingOptions = async ({
  meta,
  pageParam,
}: QueryFunctionContext<any, number>): Promise<OptionsPage> => {
  const page = pageParam ?? 0;
  const {
    getOptionFromSearchResult = defaultGetOptionFromSearchResult,
    ...params
  } = meta || ({} as any);
  const result = await searchClassifierMappings({
    ...params,
    searchText: (meta?.searchText ?? '') as string,
    page,
    size: AUTOCOMPLETE_PAGE_SIZE,
  });

  const content: OptionType[] = result.content.map(getOptionFromSearchResult);
  return {
    content,
    last: result.last,
    number: result.number,
  };
};

type GetOptionFromSearchResult = (
  result: ClassifierMappingSearchResultType,
) => OptionType;

const defaultGetOptionFromSearchResult: GetOptionFromSearchResult = ({
  code,
  name,
}) => ({
  value: code,
  label: `${code} - ${name}`,
});

export function useInfiniteSearchClassifierMappingsQuery(
  term: string,
  options?: ClassifierMappingSearchOptions,
) {
  return useInfiniteSearchQuery(
    ['search-classifier-mappings-autocomplete'],
    searchClassifierMappingOptions,
    term,
    options,
  );
}
